<script setup lang="ts">
/* eslint-disable max-lines */
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import Autocomplete from '@/components/Shared/Input/Autocomplete/Autocomplete.vue';
import { SearchService } from '@/core/sourcing/search/search.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { CustomerSuccessService } from '@/core/customer-success/customer-success.service';
import { isRequired } from '@/utils/forms';
import { ErrorService } from '@/core/shared/errors/error.service';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import CopySvg from '@/assets/svg/conversations/copy-01.svg?component';
import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import {
  type LaborMarketReportType,
  LocationScope,
} from '@/core/customer-success/customer-success.persistence';
import {
  MarketingReportType,
  type ReportCandidate,
  US_STATES,
  distanceItems,
  reportTypeItems,
  scopeOptions,
} from '@/components/LaborMarket/labor-market-report-options';
import { useResumeUpload } from '@/composables/useResumeUpload';
import ResumeDropzone from '@/components/Shared/ResumeUpload/ResumeDropzone.vue';
import JobMatchCandidateRow from '@/components/LaborMarket/JobMatchCandidateRow.vue';
import JobMatchResumeRow from '@/components/LaborMarket/JobMatchResumeRow.vue';
import { useAddressPredictions } from '@/composables/useAddressPredictions';

const dialogService = new DialogService();
const searchService = new SearchService();
const customerSuccessService = new CustomerSuccessService();
const {
  selectedFiles,
  addFiles,
  removeFile,
  toBase64,
  resetState: resetResumes,
} = useResumeUpload();

const isOpen = computed(() => dialogService.isLaborMarketReportsModalOpen);

const display = useDisplay();
// Fullscreen edge-to-edge on mobile so the tall form + actions can't be clipped
// by the fixed desktop dialog height.
const isMobile = computed(() => display.smAndDown.value);

// Success modal state
const showSuccessModal = ref(false);
const successReferenceId = ref('');
const successMessage = ref('');
const isCopied = ref(false);
const isSubmitting = ref(false);

// Form data
const jobTitle = ref('');
const location = ref<string | undefined>(undefined);
const jobTitleInput = ref<HTMLInputElement | null>(null);
const defaultDistance = 30;
const distance = ref(defaultDistance);
const reportType = ref<MarketingReportType>(MarketingReportType.STANDARD);

const isJobMatch = computed(() => reportType.value === MarketingReportType.JOB_MATCH);

const reportCandidates = ref<ReportCandidate[]>([]);
const jobMatchReportCount = ref(0);

const jobMatchIsValid = computed(
  () => reportCandidates.value.length > 0 || selectedFiles.value.length > 0,
);

function removeCandidate(userProfileId: number): void {
  reportCandidates.value = reportCandidates.value.filter((c) => c.userProfileId !== userProfileId);
}

function clearAllSelections(): void {
  reportCandidates.value = [];
  resetResumes();
}

function onResumeFilesSelected(files: File[]): void {
  addFiles(files);
}

// Single submit entry point. Both the form's submit event and the submit button's
// click can fire for one user action; route by mode and guard against re-entrancy so
// the labor-market path never runs in Job Match mode (which caused a duplicate modal).
function onFormSubmit(): void {
  if (isSubmitting.value) return;
  if (isJobMatch.value) {
    handleJobMatchSubmit();
  } else {
    handleSubmit();
  }
}

// Location scope state
const locationScope = ref<LocationScope>(LocationScope.METRO);
const selectedState = ref<string>('');

const {
  isLoading: isLoadingLocations,
  listItems: listOfLocations,
  search: onSearchLocation,
  searchText: searchLocationText,
} = useAddressPredictions((address) => searchService.getAddressPredictions(address));

// Location focus state
const isLocationFocused = ref(false);
function onLocationFocused(value: boolean) {
  isLocationFocused.value = value;
}

// Computed properties for no data text
const noDataText = computed(() =>
  searchLocationText.value.trim() === '' ? 'Type to search' : 'No results',
);

const isRelocation = computed(() => reportType.value === 'RELOCATION');

const formIsValid = computed(() => {
  if (!jobTitle.value) return false;

  // Relocation: just needs location (original behavior)
  if (isRelocation.value) {
    return !!location.value;
  }

  // Standard: depends on scope
  switch (locationScope.value) {
    case LocationScope.NATION:
      return true;
    case LocationScope.STATE:
      return !!selectedState.value;
    default:
      // LocationScope.METRO
      return !!location.value && !!distance.value;
  }
});

// Reset form when modal opens
watch(
  isOpen,
  (open) => {
    if (open) {
      resetForm();
      const context = dialogService.laborMarketReportContext;
      if (context) {
        reportType.value = context.defaultType;
        reportCandidates.value = [...context.candidates];
      }
      // Focus the job title input after a short delay to ensure the modal is fully rendered
      setTimeout(() => {
        if (jobTitleInput.value) jobTitleInput.value.focus();
      }, 100);
    }
  },
  { immediate: true },
);

// Form submission
async function handleSubmit() {
  isSubmitting.value = true;
  try {
    // Build request parameters based on report type
    let locationValue: string | undefined;
    let distanceValue: number | undefined;
    let scopeValue: LocationScope;

    if (isRelocation.value) {
      // Relocation: use original behavior (just location, no scope)
      locationValue = location.value;
      distanceValue = undefined;
      scopeValue = LocationScope.METRO;
    } else {
      // Standard: build based on scope
      if (locationScope.value === LocationScope.STATE) {
        const stateObj = US_STATES.find((s) => s.id === selectedState.value);
        locationValue = stateObj ? `${stateObj.title}, USA` : selectedState.value;
      } else {
        locationValue = location.value;
      }
      distanceValue = distance.value;
      scopeValue = locationScope.value;
    }

    // Call the API to submit the labor market report request
    const response = await customerSuccessService.submitLaborMarketReportRequest(
      jobTitle.value,
      locationValue,
      distanceValue,
      reportType.value as LaborMarketReportType,
      scopeValue,
    );

    if (response && response.success) {
      // Store success information for the modal
      successReferenceId.value = response.referenceId;
      successMessage.value = response.message;

      // Close the request modal and show the success modal
      dialogService.isLaborMarketReportsModalOpen = false;
      dialogService.laborMarketReportContext = null;
      showSuccessModal.value = true;

      // Reset form
      resetForm();
    } else {
      // Handle error case
      SnackbarService.critical(response?.message || 'Failed to submit request. Please try again.');
    }
  } catch (error) {
    // Handle unexpected errors
    SnackbarService.critical('An unexpected error occurred. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
}

async function handleJobMatchSubmit() {
  isSubmitting.value = true;
  try {
    const resumes = await Promise.all(
      selectedFiles.value.map(async (file) => ({
        resumeBase64: await toBase64(file),
        resumeFileName: file.name,
      })),
    );

    const candidates = reportCandidates.value.map((c) => ({ userProfileId: c.userProfileId }));
    const total = candidates.length + resumes.length;

    const { successCount, failureCount, batchId } =
      await customerSuccessService.submitJobMatchReport({ candidates, resumes });

    if (successCount === 0) {
      SnackbarService.critical('We could not queue your report. Please try again.');
      return;
    }

    if (failureCount > 0) {
      SnackbarService.critical(
        `${successCount} of ${total} reports were queued. ${failureCount} could not be processed.`,
      );
    }

    jobMatchReportCount.value = successCount;
    successReferenceId.value = batchId;
    dialogService.isLaborMarketReportsModalOpen = false;
    dialogService.laborMarketReportContext = null;
    showSuccessModal.value = true;
    resetForm();
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('An unexpected error occurred. Please try again.');
  } finally {
    isSubmitting.value = false;
  }
}

// Close success modal
function closeSuccessModal() {
  showSuccessModal.value = false;
  jobMatchReportCount.value = 0;
  dialogService.laborMarketReportContext = null;
}

// Form cancellation
function handleCancel() {
  resetForm();
  dialogService.laborMarketReportContext = null;
  dialogService.isLaborMarketReportsModalOpen = false;
}

// Reset form fields
function resetForm() {
  jobTitle.value = '';
  location.value = undefined;
  distance.value = defaultDistance;
  reportType.value = MarketingReportType.STANDARD;
  locationScope.value = LocationScope.METRO;
  selectedState.value = '';
  reportCandidates.value = [];
  resetResumes();
}

// Copy reference ID to clipboard
function copyReferenceId() {
  if (successReferenceId.value) {
    navigator.clipboard
      .writeText(successReferenceId.value)
      .then(() => {
        isCopied.value = true;
        setTimeout(() => {
          isCopied.value = false;
        }, 1500); // Match ProHeaderTooltip's timeout
      })
      .catch((error) => {
        ErrorService.captureException(error);
        SnackbarService.critical('Failed to copy reference ID');
      });
  }
}
</script>

<template>
  <v-dialog
    :model-value="isOpen && !showSuccessModal"
    :max-width="isMobile ? undefined : '600px'"
    :height="isMobile ? undefined : '550px'"
    :fullscreen="isMobile"
    persistent
  >
    <div
      :class="
        isMobile
          ? 'h-full overflow-y-auto bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'
          : 'rounded-2xl bg-white p-8'
      "
    >
      <!-- Mobile fullscreen: always-reachable sticky X + top safe-area inset -->
      <div
        v-if="isMobile"
        class="sticky top-0 z-10 -mx-4 flex justify-end bg-white px-4 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]"
      >
        <button
          type="button"
          aria-label="Close"
          class="-mr-2 flex h-11 w-11 cursor-pointer items-center justify-center"
          @click="handleCancel"
        >
          <img src="@/assets/svg/close-black.svg" alt="" width="20" height="20" />
        </button>
      </div>

      <!-- Header -->
      <div class="mb-6">
        <img
          v-if="!isMobile"
          class="float-right mt-1 cursor-pointer"
          src="@/assets/svg/close-black.svg"
          alt="close"
          width="20"
          height="20"
          @click="handleCancel"
        />
        <p class="font-serif text-2xl font-black text-copilot">Request a labor market report</p>
        <p class="font-sans font-normal text-shade-800">
          We'll email you a custom report based on your criteria
        </p>
      </div>

      <!-- Form -->
      <form @submit.prevent="onFormSubmit" class="flex flex-col gap-4">
        <!-- Report Type selector -->
        <div>
          <label for="reportType" class="mb-1 block text-sm font-medium text-shade-900">
            Report type
          </label>
          <v-select
            id="reportType"
            v-model="reportType"
            :items="reportTypeItems"
            item-title="title"
            item-value="id"
            density="compact"
            variant="solo"
            hide-details
            :menu-icon="ChevronIcon"
            :menu-props="{ contentClass: 'pt-1' }"
            class="report-type-select"
          >
            <template #selection="{ item }">
              <span class="text-sm text-shade-900">{{ item.title }}</span>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item
                v-bind="itemProps"
                :title="undefined"
                :ripple="false"
                class="report-type-option"
              >
                <div class="flex flex-col gap-0.5">
                  <span
                    class="text-sm text-shade-900"
                    :class="reportType === item.raw.id ? 'font-bold' : 'font-medium'"
                    >{{ item.title }}</span
                  >
                  <span class="text-xs leading-snug text-shade-800">{{
                    item.raw.description
                  }}</span>
                </div>
              </v-list-item>
            </template>
          </v-select>
        </div>

        <!-- JOB MATCH: resume dropzone + unified candidate/resume list -->
        <template v-if="isJobMatch">
          <ResumeDropzone @files-selected="onResumeFilesSelected" />

          <div v-if="reportCandidates.length || selectedFiles.length">
            <div class="mb-1 flex items-center justify-between">
              <span class="text-base font-bold text-shade-900">
                Candidates ({{ reportCandidates.length + selectedFiles.length }})
              </span>
              <button
                type="button"
                class="text-sm font-medium text-highlight-600 hover:underline"
                @click.prevent="clearAllSelections"
              >
                Clear all
              </button>
            </div>
            <div class="max-h-[220px] divide-y divide-tint-60 overflow-y-auto pr-1">
              <JobMatchCandidateRow
                v-for="candidate in reportCandidates"
                :key="`candidate-${candidate.userProfileId}`"
                :name="candidate.name"
                :subtitle="candidate.subtitle"
                @remove="removeCandidate(candidate.userProfileId)"
              />
              <JobMatchResumeRow
                v-for="(file, index) in selectedFiles"
                :key="`file-${file.name}-${index}`"
                :name="file.name"
                :size="file.size"
                @remove="removeFile(index)"
              />
            </div>
          </div>
        </template>

        <template v-if="!isJobMatch">
          <!-- Job Title field (required) -->
          <div>
            <label for="jobTitle" class="mb-1 block text-sm font-medium text-shade-900">
              Job title<span class="text-red-500">*</span>
            </label>
            <input
              id="jobTitle"
              v-model="jobTitle"
              type="text"
              ref="jobTitleInput"
              class="w-full rounded-md border border-tint-80 px-3 py-2 text-sm text-shade-900 placeholder:text-shade-800 focus:outline-none focus:ring-2 focus:ring-highlight-500"
              :rules="[isRequired]"
              placeholder="Enter job title"
            />
          </div>

          <!-- Location scope toggle (STANDARD only) -->
          <div v-if="!isRelocation">
            <div class="flex flex-wrap gap-2">
              <div
                v-for="option in scopeOptions"
                :key="option.value"
                @click="locationScope = option.value"
                :class="{
                  'bg-black text-white': locationScope === option.value,
                  'bg-white text-shade-900': locationScope !== option.value,
                }"
                class="flex h-8 cursor-pointer items-center rounded-full border border-tint-80 px-3 py-1.5 text-sm font-normal transition-colors"
              >
                {{ option.label }}
              </div>
            </div>
          </div>

          <!-- RELOCATION: Just location field (original behavior) -->
          <div v-if="isRelocation">
            <label for="location" class="mb-1 block text-sm font-medium text-shade-900">
              Location<span class="text-red-500">*</span>
            </label>
            <autocomplete
              v-model="location"
              placeholder="Enter a location"
              :items="listOfLocations"
              :loading="isLoadingLocations"
              @update:search="onSearchLocation"
              @update:focused="onLocationFocused"
              :no-data-text="noDataText"
              :rules="[isRequired]"
              open-text="Type to search"
              no-filter
              clearable
              plain
            />
          </div>

          <!-- STANDARD: METRO FIELDS -->
          <template v-if="!isRelocation && locationScope === LocationScope.METRO">
            <!-- Location field with autocomplete -->
            <div>
              <label for="location" class="mb-1 block text-sm font-medium text-shade-900">
                Location<span class="text-red-500">*</span>
              </label>
              <autocomplete
                v-model="location"
                placeholder="Enter a location"
                :items="listOfLocations"
                :loading="isLoadingLocations"
                @update:search="onSearchLocation"
                @update:focused="onLocationFocused"
                :no-data-text="noDataText"
                :rules="[isRequired]"
                open-text="Type to search"
                no-filter
                clearable
                plain
              />
            </div>

            <!-- Distance field -->
            <div>
              <label for="distance" class="mb-1 block text-sm font-medium text-shade-900">
                Distance<span class="text-red-500">*</span>
              </label>
              <autocomplete
                v-model="distance"
                :items="distanceItems"
                item-title="title"
                item-value="id"
                :rules="[isRequired]"
                plain
              />
            </div>
          </template>

          <!-- STANDARD: STATEWIDE FIELDS -->
          <div v-if="!isRelocation && locationScope === LocationScope.STATE">
            <label for="state" class="mb-1 block text-sm font-medium text-shade-900">
              State<span class="text-red-500">*</span>
            </label>
            <autocomplete
              v-model="selectedState"
              placeholder="Select a state"
              :items="US_STATES"
              item-title="title"
              item-value="id"
              :rules="[isRequired]"
              plain
            />
          </div>

          <!-- STANDARD: NATIONWIDE INFO -->
          <div
            v-if="!isRelocation && locationScope === LocationScope.NATION"
            class="rounded-lg bg-tint-40 p-3 text-sm text-shade-800"
          >
            This report will look at talent and demand across the entire United States.
          </div>
        </template>

        <!-- Actions: stack full-width on mobile, right-aligned row on desktop -->
        <div class="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-0">
          <v-btn
            :ripple="false"
            type="button"
            class="modal-button-text min-h-[44px] w-full sm:mr-4 sm:min-h-0 sm:w-auto"
            variant="flat"
            @click="handleCancel"
            >Cancel</v-btn
          >
          <v-btn
            v-if="isJobMatch"
            :ripple="false"
            class="modal-button-primary min-h-[44px] w-full sm:min-h-0 sm:w-auto"
            variant="flat"
            type="button"
            data-test="job-match-submit"
            :disabled="!jobMatchIsValid || isSubmitting"
            :loading="isSubmitting"
            @click.prevent="handleJobMatchSubmit"
          >
            Request report
          </v-btn>
          <v-btn
            v-else
            :ripple="false"
            class="modal-button-primary min-h-[44px] w-full sm:min-h-0 sm:w-auto"
            variant="flat"
            type="button"
            :disabled="!formIsValid || isSubmitting"
            :loading="isSubmitting"
            @click.prevent="handleSubmit"
          >
            Request report
          </v-btn>
        </div>
      </form>
    </div>
  </v-dialog>

  <BaseModal
    :model-value="showSuccessModal"
    :show-heading="false"
    :show-cancel-button="false"
    :fullscreen-on-mobile="true"
    submit-button-text="Close"
    :modal-class="'rounded-lg'"
    @submit="closeSuccessModal"
    @cancel="closeSuccessModal"
  >
    <template #header>
      <div>
        <div class="text-center font-serif text-2xl font-black text-copilot">
          Thank you for your request
        </div>
      </div>
    </template>

    <div class="mb-6 mt-4 flex flex-col items-center">
      <p class="mb-4 text-center text-base text-shade-800">
        <template v-if="jobMatchReportCount > 0">
          Your Job Match Reports are being generated and will be emailed to you shortly. Please keep
          your reference ID for future inquiries.
        </template>
        <template v-else>
          Your labor market report is being generated and will be emailed to you shortly. Please
          keep your reference ID for future inquiries.
        </template>
      </p>
      <div class="mt-2 rounded-lg bg-tint-40 px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-sm text-shade-600">
              Reference ID: <span class="font-bold">{{ successReferenceId }}</span>
            </span>
            <button
              class="relative ml-2 flex items-center justify-center rounded-full p-1 transition-colors"
              @click="copyReferenceId"
            >
              <CopySvg class="h-4 w-4 cursor-pointer text-shade-800 hover:text-shade-900" />
              <transition name="fade">
                <div
                  v-if="isCopied"
                  class="absolute -top-8 left-1/2 z-50 -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white"
                >
                  Copied!
                </div>
              </transition>
            </button>
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <div class="flex justify-center">
        <button
          class="rounded-lg bg-copilot px-6 py-2 text-sm font-medium text-white hover:bg-copilot/90"
          @click="closeSuccessModal"
        >
          Close
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.modal-button-text {
  @apply !h-auto !bg-transparent !px-4 !py-2 !text-sm !font-bold;
  @apply !normal-case !text-shade-800 !no-underline;
}

.modal-button-primary {
  @apply !h-auto !bg-shade-880 !px-4 !py-2 !text-sm !font-semibold;
  @apply !normal-case !text-white !no-underline;
  @apply !transition-colors !duration-300;
}

.modal-button-primary:disabled {
  @apply pointer-events-none !bg-tint-80 !text-tint-400;
}

/* Report type select — match the plain autocomplete styling used by the other fields */
.report-type-select :deep(.v-field) {
  @apply w-full rounded-md border border-tint-80 bg-white;
  box-shadow: none;
}

.report-type-select :deep(.v-field.v-field--focused) {
  @apply outline-none ring-2 ring-highlight-500;
}

.report-type-select :deep(.v-field__input) {
  @apply text-sm text-shade-900;
}

.report-type-select :deep(.v-icon) {
  @apply text-shade-900;
}

.report-type-option {
  @apply cursor-pointer;
}

/* Make autocomplete placeholder consistent with regular inputs */
:deep(.v-field__input input::placeholder) {
  font-size: inherit !important;
  opacity: 1 !important;
}

/* Ensure location field placeholder is visible */
:deep(.plain-autocomplete .v-field__input input::placeholder) {
  color: #64748b !important; /* shade-800 */
  opacity: 1 !important;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
